import { useMemo } from "react";

interface HyroxRaceTimelineProps {
  data: Record<string, unknown>;
}

interface TimelineSegment {
  label: string;
  type: "run" | "station";
  seconds: number;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const HYROX_STATIONS = [
  "ski_erg",
  "sled_push",
  "sled_pull",
  "burpee_broad_jump",
  "rowing",
  "farmers_carry",
  "sandbag_lunges",
  "wall_balls",
];

export function HyroxRaceTimeline({ data }: HyroxRaceTimelineProps) {
  const segments = useMemo(() => {
    const stationTimes = data.station_times as Record<string, { latest_value?: number }> | undefined;
    const runSplits = data.run_splits as Array<{ value?: number }> | undefined;

    if (!stationTimes && !runSplits) return [];

    const result: TimelineSegment[] = [];
    for (let i = 0; i < 8; i++) {
      // Run segment
      const runTime = runSplits?.[i]?.value ?? 0;
      result.push({ label: `Run ${i + 1}`, type: "run", seconds: runTime });

      // Station segment
      const stationName = HYROX_STATIONS[i];
      const stationTime = stationTimes?.[stationName]?.latest_value ?? 0;
      result.push({
        label: stationName.replace(/_/g, " "),
        type: "station",
        seconds: stationTime,
      });
    }
    return result;
  }, [data]);

  const overallTime = typeof data.overall_time === "number" ? data.overall_time : null;

  if (!segments.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Race Timeline</h3>
        <div className="h-[160px] flex items-center justify-center text-muted-foreground text-sm">
          No race timeline data available.
        </div>
      </div>
    );
  }

  const maxSeconds = Math.max(...segments.map((s) => s.seconds), 1);

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Race Timeline</h3>
        {overallTime !== null && (
          <span className="text-xs font-mono tabular-nums text-foreground">
            Total: {formatTime(overallTime as number)}
          </span>
        )}
      </div>
      <div className="flex gap-0.5 h-8 rounded overflow-hidden">
        {segments.map((seg, i) => {
          const widthPct = Math.max((seg.seconds / maxSeconds) * 6, 1);
          return (
            <div
              key={`${seg.label}-${i}`}
              className={`relative group ${seg.type === "run" ? "bg-blue-500/50" : "bg-emerald-500/50"}`}
              style={{ flex: widthPct }}
              title={`${seg.label}: ${formatTime(seg.seconds)}`}
            >
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-[8px] font-mono text-white">{formatTime(seg.seconds)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-sm bg-blue-500/50" />
          <span className="text-[10px] text-muted-foreground">Run</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-sm bg-emerald-500/50" />
          <span className="text-[10px] text-muted-foreground">Station</span>
        </div>
      </div>
    </div>
  );
}
